/**
 * Form-level message.
 *
 * `role="alert"` so it is announced when it appears — a submission failure that only changes colour
 * is silent to anyone not looking at that part of the screen.
 *
 * When the server supplied a correlation id it is shown as a Reference ID. That is the value support
 * needs to find the server-side event, and it lets a user report a problem without describing
 * private planning content (docs/engineering/07-api-conventions.md).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import './primitives.css';

export interface NoticeProps {
  children: ReactNode;
  tone?: 'error' | 'success';
  referenceId?: string | undefined;
}

export function Notice({ children, tone = 'error', referenceId }: NoticeProps): React.JSX.Element {
  const { i18n } = useTranslation(['errors', 'common']);

  let content = children;
  if (typeof children === 'string') {
    if (i18n.exists(children, { ns: 'errors', keySeparator: false })) {
      content = i18n.t(children, { ns: 'errors', keySeparator: false });
    } else if (i18n.exists(children, { ns: 'common', keySeparator: false })) {
      content = i18n.t(children, { ns: 'common', keySeparator: false });
    }
  }

  return (
    <div
      className={`notice${tone === 'success' ? ' notice--success' : ''}`}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      {content}
      {referenceId ? (
        <code className="notice__reference">
          {i18n.t('reference_id', { ns: 'errors', defaultValue: 'Reference ID' })} · {referenceId}
        </code>
      ) : null}
    </div>
  );
}
